import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Gear,
  SignIn,
  SignOut,
  UserCircle,
  UserPlus,
} from '@phosphor-icons/react';
import {selectIsLoggedIn, selectUserInfo, UserInfo} from '@smartmixin/core';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import isEqual from 'react-fast-compare';
import {useIntl} from 'react-intl';
import {useLocation} from 'react-router';
import {IconButton} from '../../../components/ui';
import RouterLink from '../../../lib/RouterLink';
import {useLogout} from '../../../navigation/layout/useLogout';
import {useSelector} from '../../../store';

const MENU_ID = 'menu-appbar';

// An outlined card (hairline, no shadow) under the button's right edge; the
// items inset with rounded corners, the label in body2 medium and the
// e-mail under the name in caption.
const MENU_SX = {
  '& .MuiPaper-root': {minWidth: 200},
  '& .MuiMenuItem-root': {
    mx: 1,
    mb: 0.5,
    borderRadius: 1,
  },
  '& .MuiListItemText-primary': {
    typography: 'body2',
    fontWeight: 'fontWeightMedium',
  },
  '& .MuiListItemText-secondary': {typography: 'caption'},
} as const;

/**
 * The account menu of the top bar: signed in, the account (name and
 * e-mail), Settings and Log out; signed out, Settings, Log in and Register.
 *
 * The items are the Menu's own children, so the menu is ONE role=menu whose
 * items the arrow keys walk. They were in a MenuList nested inside the
 * Menu's: a menu inside a menu, whose first "item" was the inner list.
 */
const UserMenu = () => {
  const {formatMessage} = useIntl();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const {email, firstName, lastName}: UserInfo = useSelector(
    selectUserInfo,
    isEqual,
  );
  const location = useLocation();
  // As the sidebar's account row: sign out, then go home.
  const logout = useLogout();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const openMenu = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const settings = (
    <MenuItem key="settings" component={RouterLink} to="/app/settings">
      <ListItemIcon>
        <Gear />
      </ListItemIcon>
      <ListItemText primary={formatMessage(messages.settings.settings)} />
    </MenuItem>
  );

  // An array, not a Fragment: Menu walks its direct children.
  const items = isLoggedIn
    ? [
        <MenuItem
          key="account"
          component={RouterLink}
          to="/app/settings/account">
          <ListItemIcon>
            <UserCircle />
          </ListItemIcon>
          <ListItemText
            primary={[firstName, lastName].filter(Boolean).join(' ')}
            secondary={email}
          />
        </MenuItem>,
        <Divider key="divider-account" />,
        settings,
        <Divider key="divider-settings" />,
        <MenuItem key="logout" onClick={logout}>
          <ListItemIcon>
            <SignOut />
          </ListItemIcon>
          <ListItemText primary={formatMessage(messages.auth.logout)} />
        </MenuItem>,
      ]
    : [
        settings,
        <Divider key="divider-settings" />,
        <MenuItem
          key="login"
          component={RouterLink}
          to="/auth/login"
          state={{from: location}}>
          <ListItemIcon>
            <SignIn />
          </ListItemIcon>
          <ListItemText primary={formatMessage(messages.auth.login)} />
        </MenuItem>,
        <MenuItem
          key="register"
          component={RouterLink}
          to="/auth/register"
          state={{from: location}}>
          <ListItemIcon>
            <UserPlus />
          </ListItemIcon>
          <ListItemText primary={formatMessage(messages.auth.register)} />
        </MenuItem>,
      ];

  return (
    <>
      <IconButton
        aria-label={formatMessage(messages.account.accountSection)}
        aria-controls={MENU_ID}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={openMenu}>
        <UserCircle />
      </IconButton>
      <Menu
        id={MENU_ID}
        anchorEl={anchorEl}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        keepMounted
        open={open}
        onClick={closeMenu}
        onClose={closeMenu}
        slotProps={{paper: {variant: 'outlined', elevation: 0}}}
        sx={MENU_SX}>
        {items}
      </Menu>
    </>
  );
};

export default UserMenu;
